/**
 * ChecklistGroup Component
 *
 * Groups related checklist items under a category with collapsible sections.
 * Used for organizing MHMS audit checklists, pre-audit reviews, and document verification.
 *
 * Features:
 * - Category-based grouping
 * - Collapsible sections
 * - Group-level progress indicator
 * - Bulk actions (complete all, reset all)
 * - Visual hierarchy with indentation
 * - Keyboard navigation support
 *
 * Examples:
 * - MHMS Categories: Management Responsibility, Halal Management System, Traceability
 * - Document Categories: Licenses, Certifications, SOPs
 * - Pre-audit Categories: Documentation, Facility, Training
 */

import React, { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Check, RotateCcw } from 'lucide-react';
import type { ChecklistItem as ChecklistItemType, ChecklistItemState } from '../../hooks/useChecklistState';
import { ChecklistItem } from './ChecklistItem';

export interface ChecklistGroupProps {
  title: string;
  description?: string;
  items: ChecklistItemType[];
  itemStates: Record<string, ChecklistItemState>;
  onStatusChange?: (id: string, status: ChecklistItemState['status']) => void;
  onNotesChange?: (id: string, notes: string) => void;
  icon?: ReactElement;
  defaultExpanded?: boolean;
  disabled?: boolean;
  showProgress?: boolean;
  compact?: boolean;
  // Bulk actions
  onCompleteAll?: () => void;
  onResetAll?: () => void;
}

export const ChecklistGroup: React.FC<ChecklistGroupProps> = ({
  title,
  description,
  items,
  itemStates,
  onStatusChange,
  onNotesChange,
  icon,
  defaultExpanded = true,
  disabled = false,
  showProgress = true,
  compact = false,
  onCompleteAll,
  onResetAll,
}) => {
  const { t } = useTranslation(['common', 'auditor']);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Calculate progress for this group
  const calculateProgress = () => {
    const total = items.length;
    let completed = 0;
    let pending = 0;
    let notApplicable = 0;
    let failed = 0;

    items.forEach((item) => {
      const state = itemStates[item.id];
      const status = state?.status || 'pending';

      if (status === 'completed') completed++;
      else if (status === 'pending') pending++;
      else if (status === 'not-applicable') notApplicable++;
      else if (status === 'failed') failed++;
    });

    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    return {
      total,
      completed,
      pending,
      notApplicable,
      failed,
      percentage,
    };
  };

  const progress = calculateProgress();

  const getProgressColor = () => {
    if (progress.failed > 0) return 'text-red-600';
    if (progress.percentage === 100) return 'text-green-600';
    if (progress.percentage >= 50) return 'text-blue-600';
    return 'text-gray-600';
  };

  const getProgressBarColor = () => {
    if (progress.failed > 0) return 'bg-red-500';
    if (progress.percentage === 100) return 'bg-green-500';
    if (progress.percentage >= 50) return 'bg-blue-500';
    return 'bg-gray-400';
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
      {/* Group Header */}
      <div className="bg-gray-50 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
          aria-expanded={isExpanded}
          disabled={disabled}
        >
          <div className="flex items-center gap-3 flex-1">
            {/* Expand/Collapse Icon */}
            <ChevronRight
              className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            />

            {/* Group Icon */}
            {icon && <div className="flex-shrink-0">{icon}</div>}

            {/* Title and Description */}
            <div className="text-left flex-1">
              <h3 className="font-semibold text-gray-900">{title}</h3>
              {description && !compact && (
                <p className="text-sm text-gray-600 mt-0.5">{description}</p>
              )}
            </div>
          </div>

          {/* Progress Info */}
          {showProgress && (
            <div className="flex items-center gap-4 ml-4">
              <div className={`text-sm font-medium ${getProgressColor()}`}>
                {progress.completed} / {progress.total}
                {progress.failed > 0 && (
                  <span className="text-red-600 ml-2">
                    ({progress.failed} {t('common:status.failed')})
                  </span>
                )}
              </div>
              <div className="text-lg font-bold text-gray-700">
                {progress.percentage}%
              </div>
            </div>
          )}
        </button>

        {/* Progress Bar */}
        {showProgress && (
          <div className="h-1 bg-gray-200">
            <div
              className={`h-full transition-all ${getProgressBarColor()}`}
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
        )}

        {/* Bulk Actions */}
        {isExpanded && !disabled && (onCompleteAll || onResetAll) && (
          <div className="px-4 py-2 bg-white border-t border-gray-200 flex gap-2">
            {onCompleteAll && (
              <button
                type="button"
                onClick={onCompleteAll}
                className="px-3 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-300 rounded hover:bg-green-100 transition-colors flex items-center gap-1"
              >
                <Check className="w-3 h-3" />
                {t('common:actions.completeAll')}
              </button>
            )}
            {onResetAll && (
              <button
                type="button"
                onClick={onResetAll}
                className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded hover:bg-gray-100 transition-colors flex items-center gap-1"
              >
                <RotateCcw className="w-3 h-3" />
                {t('common:actions.resetAll')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Group Items */}
      {isExpanded && (
        <div className={`${compact ? 'p-2' : 'p-4'} space-y-3 bg-gray-50`}>
          {items.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <p>{t('common:messages.noItems')}</p>
            </div>
          ) : (
            items.map((item) => {
              const itemState = itemStates[item.id] || {
                id: item.id,
                status: 'pending' as const,
              };

              return (
                <ChecklistItem
                  key={item.id}
                  id={item.id}
                  label={item.label}
                  description={item.description}
                  status={itemState.status}
                  notes={itemState.notes}
                  reference={item.reference}
                  required={item.required}
                  completedAt={itemState.completedAt}
                  completedBy={itemState.completedBy}
                  disabled={disabled}
                  onStatusChange={onStatusChange}
                  onNotesChange={onNotesChange}
                  compact={compact}
                />
              );
            })
          )}
        </div>
      )}

      {/* Footer Summary (when collapsed) */}
      {!isExpanded && showProgress && (
        <div className="px-4 py-2 bg-white text-xs text-gray-600 flex gap-4">
          <span>{t('common:status.completed')}: {progress.completed}</span>
          <span>{t('common:status.pending')}: {progress.pending}</span>
          {progress.notApplicable > 0 && (
            <span>{t('common:status.notApplicable')}: {progress.notApplicable}</span>
          )}
          {progress.failed > 0 && (
            <span className="text-red-600">{t('common:status.failed')}: {progress.failed}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default ChecklistGroup;
